// Correct a lead, or add one by hand. Both go the same way: the form, then a
// dry run whose changes are shown for review, then the save on confirm.
//
// The server is the authority on every rule (LeadEdits.java); leadEditRules
// mirrors the limits so most mistakes are caught before a request, and the
// server's own message is shown for the rest.
import React, { useState } from 'react';
import { AlertCircle, Loader2, Plus, Trash2, X } from 'lucide-react';
import {
  capturesService, Capture, LeadCreateBody, LeadUpdateBody, LeadWriteResult,
} from 'api/services/captures.service';
import { labelFor } from 'utils/capturedFields';
import {
  EMPTY_CREATE_DRAFT, LEAD_STATUSES, LEAD_STATUS_LABELS, LIMITS, LeadCreateDraft, LeadEditDraft,
  buildCreateBody, buildUpdateBody, changeLabel, changeValue, draftFromCapture, isEmptyUpdate,
  validateCreate, validateEdit,
} from './leadEditRules';

type Props =
  | { mode: 'edit'; capture: Capture; onClose: () => void; onSaved: (r: LeadWriteResult) => void }
  | { mode: 'create'; capture?: undefined; onClose: () => void; onSaved: (r: LeadWriteResult) => void };

type Step = 'form' | 'review' | 'done';

const label = 'block text-[12px] font-medium text-gray-700 dark:text-gray-200 mb-1';
const input = 'tl-input w-full !text-[13px]';

const CrmNotice: React.FC<{ result: LeadWriteResult }> = ({ result }) => {
  const crm = result.crm;
  if (!crm || crm.crmUpdated) return null;
  return (
    <div role="alert" data-testid="crm-notice"
         className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-900
                    dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
      <p className="font-semibold">
        The CRM copy{crm.provider ? ` in ${crm.provider}` : ''} was NOT changed.
      </p>
      <p className="mt-0.5">{crm.message}</p>
    </div>
  );
};

const ChangeList: React.FC<{ result: LeadWriteResult }> = ({ result }) => (
  result.changes.length === 0 ? (
    <p className="text-[13px]" style={{ color: 'var(--text-muted)' }}>
      {result.message || 'Nothing would change.'}
    </p>
  ) : (
    <ul className="divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700"
        data-testid="change-list">
      {result.changes.map((c) => (
        <li key={c.field} className="px-3 py-2 text-[12px]">
          <span className="font-medium text-gray-900 dark:text-white">{changeLabel(c.field)}</span>
          <span style={{ color: 'var(--text-muted)' }}>: </span>
          <span className="line-through break-words" style={{ color: 'var(--text-muted)' }}>{changeValue(c.from)}</span>
          <span style={{ color: 'var(--text-muted)' }}> → </span>
          <span className="break-words text-gray-900 dark:text-white">{changeValue(c.to)}</span>
        </li>
      ))}
    </ul>
  )
);

const LeadEditModal: React.FC<Props> = (props) => {
  const { mode, onClose, onSaved } = props;
  const capture = props.mode === 'edit' ? props.capture : undefined;

  const [edit, setEdit] = useState<LeadEditDraft | null>(capture ? draftFromCapture(capture) : null);
  const [create, setCreate] = useState<LeadCreateDraft>(EMPTY_CREATE_DRAFT);
  const [step, setStep] = useState<Step>('form');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<LeadWriteResult | null>(null);
  const [done, setDone] = useState<LeadWriteResult | null>(null);
  // The exact body that was previewed, so the save sends what was reviewed.
  const [pending, setPending] = useState<LeadUpdateBody | LeadCreateBody | null>(null);

  const send = (body: LeadUpdateBody | LeadCreateBody, dryRun: boolean) =>
    mode === 'edit'
      ? capturesService.update(capture!.id, body as LeadUpdateBody, dryRun)
      : capturesService.create(body as LeadCreateBody, dryRun);

  const preview = async () => {
    setError(null);
    let body: LeadUpdateBody | LeadCreateBody;
    if (mode === 'edit') {
      const problem = validateEdit(capture!, edit!);
      if (problem) { setError(problem); return; }
      body = buildUpdateBody(capture!, edit!);
      if (isEmptyUpdate(body)) { setError('Nothing has changed yet.'); return; }
    } else {
      const problem = validateCreate(create);
      if (problem) { setError(problem); return; }
      body = buildCreateBody(create);
    }
    setBusy(true);
    const r = await send(body, true);
    setBusy(false);
    if (!r.success || !r.data) {
      setError(r.error || 'The preview failed.');
      return;
    }
    setPending(body);
    setReview(r.data);
    setStep('review');
  };

  const save = async () => {
    if (!pending) return;
    setError(null);
    setBusy(true);
    const r = await send(pending, false);
    setBusy(false);
    if (!r.success || !r.data) {
      setError(r.error || 'The save failed.');
      return;
    }
    setDone(r.data);
    setStep('done');
    onSaved(r.data);
  };

  const title = mode === 'edit' ? 'Edit lead' : 'Add lead';
  const setField = (k: string, v: string) => setEdit((d) => (d ? { ...d, fields: { ...d.fields, [k]: v } } : d));
  const setExtra = (i: number, patch: Partial<{ key: string; value: string }>) =>
    setCreate((d) => ({ ...d, extra: d.extra.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" role="dialog"
         aria-modal="true" aria-label={title}>
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
            {mode === 'edit' ? (
              <p className="text-[12px]" style={{ color: 'var(--text-muted)' }}>
                The phone number, agent, call and CRM details are not editable. An edit is saved in
                cpass only; it is never sent to a CRM.
              </p>
            ) : (
              <p className="text-[12px]" style={{ color: 'var(--text-muted)' }} data-testid="manual-note">
                Kept in cpass only. It is not sent to your CRM and starts no workflow or follow-up.
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="shrink-0 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === 'form' && mode === 'edit' && edit && (
          <div className="space-y-3">
            <div>
              <label className={label} htmlFor="lead-edit-name">Name</label>
              <input id="lead-edit-name" className={input} maxLength={LIMITS.maxName} value={edit.displayName}
                     onChange={(e) => setEdit({ ...edit, displayName: e.target.value })} />
            </div>
            <div>
              <label className={label} htmlFor="lead-edit-status">Status</label>
              <select id="lead-edit-status" className={input} value={edit.status}
                      onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
                {!edit.status && <option value="">Not set</option>}
                {LEAD_STATUSES.map((s) => <option key={s} value={s}>{LEAD_STATUS_LABELS[s]}</option>)}
              </select>
            </div>
            <div>
              <label className={label} htmlFor="lead-edit-notes">Notes</label>
              <textarea id="lead-edit-notes" className={input} rows={3} maxLength={LIMITS.maxNotes} value={edit.notes}
                        onChange={(e) => setEdit({ ...edit, notes: e.target.value })} />
            </div>
            {Object.keys(edit.fields).length > 0 && (
              <fieldset className="space-y-2">
                <legend className="text-[12px] font-semibold text-gray-900 dark:text-white mb-1">Captured answers</legend>
                <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  Empty a value to blank it. A field is never removed.
                </p>
                {Object.keys(edit.fields).map((k) => (
                  <div key={k}>
                    <label className={label} htmlFor={`lead-edit-f-${k}`}>{labelFor(k)}</label>
                    <input id={`lead-edit-f-${k}`} className={input} maxLength={LIMITS.maxValue}
                           value={edit.fields[k]} onChange={(e) => setField(k, e.target.value)} />
                  </div>
                ))}
              </fieldset>
            )}
          </div>
        )}

        {step === 'form' && mode === 'create' && (
          <div className="space-y-3">
            <div>
              <label className={label} htmlFor="lead-new-phone">Phone *</label>
              <input id="lead-new-phone" className={input} placeholder="+919876543210" value={create.phone}
                     onChange={(e) => setCreate({ ...create, phone: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={label} htmlFor="lead-new-name">Name</label>
                <input id="lead-new-name" className={input} maxLength={LIMITS.maxName} value={create.name}
                       onChange={(e) => setCreate({ ...create, name: e.target.value })} />
              </div>
              <div>
                <label className={label} htmlFor="lead-new-email">Email</label>
                <input id="lead-new-email" type="email" className={input} maxLength={LIMITS.maxEmail} value={create.email}
                       onChange={(e) => setCreate({ ...create, email: e.target.value })} />
              </div>
            </div>
            <div>
              <label className={label} htmlFor="lead-new-status">Status</label>
              <select id="lead-new-status" className={input} value={create.status}
                      onChange={(e) => setCreate({ ...create, status: e.target.value })}>
                {LEAD_STATUSES.map((s) => <option key={s} value={s}>{LEAD_STATUS_LABELS[s]}</option>)}
              </select>
            </div>
            <div>
              <label className={label} htmlFor="lead-new-notes">Notes</label>
              <textarea id="lead-new-notes" className={input} rows={3} maxLength={LIMITS.maxNotes} value={create.notes}
                        onChange={(e) => setCreate({ ...create, notes: e.target.value })} />
            </div>
            <fieldset className="space-y-2">
              <legend className="text-[12px] font-semibold text-gray-900 dark:text-white mb-1">Other fields</legend>
              {create.extra.map((r, i) => (
                <div key={i} className="flex gap-2">
                  <input className={`${input} !w-2/5`} placeholder="field_name" aria-label={`Field ${i + 1} name`}
                         value={r.key} onChange={(e) => setExtra(i, { key: e.target.value })} />
                  <input className={input} placeholder="value" aria-label={`Field ${i + 1} value`} maxLength={LIMITS.maxValue}
                         value={r.value} onChange={(e) => setExtra(i, { value: e.target.value })} />
                  <button type="button" className="shrink-0 p-1" aria-label={`Remove field ${i + 1}`}
                          onClick={() => setCreate({ ...create, extra: create.extra.filter((_, j) => j !== i) })}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {create.extra.length < LIMITS.maxKeys && (
                <button type="button" className="tl-btn tl-btn-sm tl-btn-neutral"
                        onClick={() => setCreate({ ...create, extra: [...create.extra, { key: '', value: '' }] })}>
                  <Plus className="w-3.5 h-3.5" /> Add a field
                </button>
              )}
            </fieldset>
          </div>
        )}

        {step === 'review' && review && (
          <div className="space-y-3" data-testid="lead-review">
            <p className="text-[13px] font-medium text-gray-900 dark:text-white">
              {mode === 'edit' ? 'Review the changes' : 'Review the new lead'}
            </p>
            <ChangeList result={review} />
            {review.duplicate && (
              <p className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{review.message}</p>
            )}
            <CrmNotice result={review} />
          </div>
        )}

        {step === 'done' && done && (
          <div className="space-y-3" data-testid="lead-done">
            <p className="text-[13px] font-medium text-gray-900 dark:text-white">{done.message}</p>
            <ChangeList result={done} />
            <CrmNotice result={done} />
          </div>
        )}

        {error && (
          <div role="alert" className="flex gap-2 rounded-lg border border-red-300 bg-red-50 p-2.5 text-[12px] text-red-800
                          dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          {step === 'form' && (
            <>
              <button type="button" className="tl-btn tl-btn-sm tl-btn-neutral" onClick={onClose}>Cancel</button>
              <button type="button" className="tl-btn tl-btn-sm tl-btn-primary" onClick={preview} disabled={busy}>
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Review changes
              </button>
            </>
          )}
          {step === 'review' && (
            <>
              <button type="button" className="tl-btn tl-btn-sm tl-btn-neutral"
                      onClick={() => { setStep('form'); setError(null); }} disabled={busy}>
                Back
              </button>
              {review && review.changes.length > 0 && !review.duplicate && (
                <button type="button" className="tl-btn tl-btn-sm tl-btn-primary" onClick={save} disabled={busy}>
                  {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {mode === 'edit' ? 'Save changes' : 'Add lead'}
                </button>
              )}
            </>
          )}
          {step === 'done' && (
            <button type="button" className="tl-btn tl-btn-sm tl-btn-primary" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadEditModal;
